import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    Trophy, Medal, Users, Bot, Calendar, ChevronDown, ChevronRight, 
    Zap, Crown, Target, CheckCircle, XCircle, PoundSterling
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { getToken } from '../lib/auth';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper for ordinal suffixes (1st, 2nd, 3rd, 4th, etc.)
const getOrdinalSuffix = (n) => {
    const s = ["th", "st", "nd", "rd"];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

// Category info with success criteria
const CATEGORIES = {
    top_pick: { 
        label: 'Top Pick', 
        icon: Trophy, 
        criteria: 'Must WIN (1st place)'
    },
    each_way: { 
        label: 'Each-Way', 
        icon: Medal, 
        criteria: 'Must PLACE (1st-3rd)'
    },
    dark_horse: { 
        label: 'Dark Horse', 
        icon: Zap, 
        criteria: 'Must WIN (1st place)'
    }
};

// Category Card Component - Clean minimal design
const CategoryCard = ({ category, aiStats, expertStats }) => {
    const cat = CATEGORIES[category];
    const Icon = cat.icon;
    
    const aiLeading = aiStats.correct > expertStats.correct;
    const expertLeading = expertStats.correct > aiStats.correct;
    
    const expertPct = expertStats.selections > 0 ? Math.round((expertStats.correct / expertStats.selections) * 100) : 0;
    const aiPct = aiStats.selections > 0 ? Math.round((aiStats.correct / aiStats.selections) * 100) : 0;
    
    return (
        <Card className="card-hover">
            <CardContent className="pt-6">
                {/* Category Header */}
                <div className="flex items-center justify-center gap-2 mb-1">
                    <Icon className="h-5 w-5 text-primary" />
                    <span className="font-heading font-semibold">{cat.label}</span>
                </div>
                <p className="text-center text-xs text-muted-foreground mb-5">{cat.criteria}</p>
                
                {/* Score Display */}
                <div className="flex items-center justify-between gap-4">
                    {/* Podium */}
                    <div className={`flex-1 text-center p-4 rounded-lg transition-colors ${expertLeading ? 'bg-primary/10' : 'bg-muted/50'}`}>
                        <div className="flex items-center justify-center gap-1.5 mb-2">
                            <Users className="h-4 w-4 text-muted-foreground" />
                            {expertLeading && <Crown className="h-3.5 w-3.5 text-amber-500" />}
                        </div>
                        <div className={`text-2xl font-bold font-data ${expertLeading ? 'text-primary' : 'text-foreground'}`}>
                            {expertStats.correct}
                        </div>
                        <div className="text-xs text-muted-foreground">of {expertStats.selections} • {expertPct}%</div>
                    </div>
                    
                    {/* VS Divider */}
                    <div className="text-sm font-medium text-muted-foreground">vs</div>
                    
                    {/* AI */}
                    <div className={`flex-1 text-center p-4 rounded-lg transition-colors ${aiLeading ? 'bg-primary/10' : 'bg-muted/50'}`}>
                        <div className="flex items-center justify-center gap-1.5 mb-2">
                            {aiLeading && <Crown className="h-3.5 w-3.5 text-amber-500" />}
                            <Bot className="h-4 w-4 text-muted-foreground" />
                        </div>
                        <div className={`text-2xl font-bold font-data ${aiLeading ? 'text-primary' : 'text-foreground'}`}>
                            {aiStats.correct}
                        </div>
                        <div className="text-xs text-muted-foreground">of {aiStats.selections} • {aiPct}%</div>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};

// Overall Score Card - Clean minimal design with profit
const OverallScoreCard = ({ stats }) => {
    const expertTotal = stats.experts.total_correct;
    const aiTotal = stats.ai.total_correct;
    const maxCorrect = Math.max(expertTotal, aiTotal, 1);
    
    const expertProfit = stats.experts.potential_profit || 0;
    const aiProfit = stats.ai.potential_profit || 0;
    
    const expertLeading = expertTotal > aiTotal;
    const aiLeading = aiTotal > expertTotal;
    
    return (
        <Card className="glass-card">
            <CardHeader className="text-center pb-2">
                <CardTitle className="font-heading text-xl">Overall Score</CardTitle>
                <CardDescription>
                    {stats.finished_races} races completed • £10 stake per pick
                </CardDescription>
            </CardHeader>
            <CardContent>
                {/* Score Display */}
                <div className="flex items-center justify-center gap-6 sm:gap-8 mb-6">
                    {/* Podium */}
                    <div className="text-center">
                        <div className="flex items-center justify-center gap-2 mb-2">
                            <Users className="h-5 w-5 text-muted-foreground" />
                            {expertLeading && <Crown className="h-4 w-4 text-amber-500" />}
                        </div>
                        <div className="text-xs text-muted-foreground mb-1">Podium</div>
                        <div className={`text-3xl sm:text-4xl font-bold font-data ${expertLeading ? 'text-primary' : 'text-foreground'}`}>
                            {expertTotal}
                        </div>
                        <div className="text-xs text-muted-foreground">correct</div>
                        {expertProfit > 0 && (
                            <div className="mt-2 text-sm font-semibold text-green-500">
                                +£{expertProfit.toFixed(2)}
                            </div>
                        )}
                    </div>
                    
                    {/* VS */}
                    <div className="text-xl sm:text-2xl font-bold text-muted-foreground/50">vs</div>
                    
                    {/* AI */}
                    <div className="text-center">
                        <div className="flex items-center justify-center gap-2 mb-2">
                            {aiLeading && <Crown className="h-4 w-4 text-amber-500" />}
                            <Bot className="h-5 w-5 text-muted-foreground" />
                        </div>
                        <div className="text-xs text-muted-foreground mb-1">AI</div>
                        <div className={`text-3xl sm:text-4xl font-bold font-data ${aiLeading ? 'text-primary' : 'text-foreground'}`}>
                            {aiTotal}
                        </div>
                        <div className="text-xs text-muted-foreground">correct</div>
                        {aiProfit > 0 && (
                            <div className="mt-2 text-sm font-semibold text-green-500">
                                +£{aiProfit.toFixed(2)}
                            </div>
                        )}
                    </div>
                </div>
                
                {/* Progress Bars */}
                <div className="space-y-2">
                    <div className="flex items-center gap-3">
                        <Users className="h-4 w-4 text-muted-foreground shrink-0" />
                        <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                            <div 
                                className="h-full bg-primary rounded-full transition-all duration-500"
                                style={{ width: `${(expertTotal / maxCorrect) * 100}%` }}
                            />
                        </div>
                        <span className="text-sm font-data w-6 text-right">{expertTotal}</span>
                    </div>
                    <div className="flex items-center gap-3">
                        <Bot className="h-4 w-4 text-muted-foreground shrink-0" />
                        <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                            <div 
                                className="h-full bg-muted-foreground/50 rounded-full transition-all duration-500"
                                style={{ width: `${(aiTotal / maxCorrect) * 100}%` }}
                            />
                        </div>
                        <span className="text-sm font-data w-6 text-right">{aiTotal}</span>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};

// Race Result Row - Mobile-friendly design
const RaceResultRow = ({ race, expanded, onToggle }) => {
    const aiCorrect = race.ai_correct || 0;
    const expertCorrect = race.expert_correct || 0;
    const aiWins = aiCorrect > expertCorrect;
    const expertWins = expertCorrect > aiCorrect;
    
    const getResultBadge = (pick) => {
        // Non-runner - show NR in red
        if (pick.is_non_runner) {
            return (
                <Badge variant="destructive" className="text-[10px] px-1.5">
                    NR
                </Badge>
            );
        }
        // Pending (race not finished)
        if (pick.position === null && race.status !== 'finished') {
            return <Badge variant="outline" className="text-[10px] px-1.5">Pending</Badge>;
        }
        // Race finished but no position - unplaced
        if (pick.position === null && race.status === 'finished') {
            return <Badge variant="secondary" className="text-[10px] px-1.5">Unplaced</Badge>;
        }
        // Correct pick
        if (pick.correct) {
            return (
                <Badge variant="default" className="text-[10px] px-1.5">
                    {getOrdinalSuffix(pick.position)}
                </Badge>
            );
        }
        // Incorrect pick
        return (
            <Badge variant="secondary" className="text-[10px] px-1.5">
                {pick.position ? getOrdinalSuffix(pick.position) : 'DNF'}
            </Badge>
        );
    };
    
    return (
        <Collapsible open={expanded} onOpenChange={onToggle}>
            <CollapsibleTrigger asChild>
                <div className="flex items-center justify-between p-3 hover:bg-muted/50 rounded-lg cursor-pointer transition-colors">
                    <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                        {expanded ? (
                            <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
                        ) : (
                            <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                        )}
                        <div className="min-w-0">
                            <p className="font-medium text-sm truncate">{race.title}</p>
                            <p className="text-xs text-muted-foreground truncate">{race.meeting}</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 text-sm shrink-0 ml-2">
                        <div className={`flex items-center gap-1 ${expertWins ? 'text-primary font-semibold' : 'text-muted-foreground'}`}>
                            {expertWins && <Crown className="h-3 w-3 text-amber-500" />}
                            <span className="font-data">{expertCorrect}</span>
                        </div>
                        <span className="text-muted-foreground/50">-</span>
                        <div className={`flex items-center gap-1 ${aiWins ? 'text-primary font-semibold' : 'text-muted-foreground'}`}>
                            <span className="font-data">{aiCorrect}</span>
                            {aiWins && <Crown className="h-3 w-3 text-amber-500" />}
                        </div>
                    </div>
                </div>
            </CollapsibleTrigger>
            
            <CollapsibleContent>
                <div className="px-2 sm:px-4 pb-4 pt-2">
                    {/* Mobile: Stack vertically, Desktop: Side by side */}
                    <div className="space-y-3 sm:space-y-0 sm:grid sm:grid-cols-2 sm:gap-4">
                        {/* Podium picks */}
                        <div className="bg-muted/30 rounded-lg p-3">
                            <div className="flex items-center gap-2 text-muted-foreground text-xs mb-3">
                                <Users className="h-3.5 w-3.5" />
                                <span className="font-semibold">Podium</span>
                            </div>
                            <div className="space-y-2">
                                {race.expert_picks?.map((pick, idx) => {
                                    const cat = CATEGORIES[pick.type];
                                    const Icon = cat?.icon || Trophy;
                                    return (
                                        <div key={idx} className="flex items-center justify-between gap-2">
                                            <div className="flex items-center gap-1.5 min-w-0 flex-1">
                                                <Icon className="h-3 w-3 text-muted-foreground shrink-0" />
                                                <span className="text-xs truncate">{pick.name}</span>
                                            </div>
                                            {getResultBadge(pick)}
                                        </div>
                                    );
                                })}
                                {(!race.expert_picks || race.expert_picks.length === 0) && (
                                    <p className="text-xs text-muted-foreground">No picks</p>
                                )}
                            </div>
                        </div>
                        
                        {/* AI picks */}
                        <div className="bg-muted/30 rounded-lg p-3">
                            <div className="flex items-center gap-2 text-muted-foreground text-xs mb-3">
                                <Bot className="h-3.5 w-3.5" />
                                <span className="font-semibold">AI</span>
                            </div>
                            <div className="space-y-2">
                                {race.ai_picks?.map((pick, idx) => {
                                    const cat = CATEGORIES[pick.type];
                                    const Icon = cat?.icon || Trophy;
                                    return (
                                        <div key={idx} className="flex items-center justify-between gap-2">
                                            <div className="flex items-center gap-1.5 min-w-0 flex-1">
                                                <Icon className="h-3 w-3 text-muted-foreground shrink-0" />
                                                <span className="text-xs truncate">{pick.name}</span>
                                            </div>
                                            {getResultBadge(pick)}
                                        </div>
                                    );
                                })}
                                {(!race.ai_picks || race.ai_picks.length === 0) && (
                                    <p className="text-xs text-muted-foreground">No picks</p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </CollapsibleContent>
        </Collapsible>
    );
};

export default function ExpertsVsAIPage() {
    const { isAuthenticated } = useAuth();
    const navigate = useNavigate();
    
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedDay, setSelectedDay] = useState(null);
    const [expandedRaces, setExpandedRaces] = useState({});
    
    useEffect(() => {
        if (!isAuthenticated) {
            navigate('/login');
            return;
        }
        fetchStats();
    }, [isAuthenticated, navigate]);
    
    const fetchStats = async () => {
        try {
            const token = getToken();
            const res = await axios.get(`${API_URL}/api/ai-vs-experts/stats`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setStats(res.data);
            
            // Auto-select first valid day
            const days = Object.keys(res.data.daily_breakdown || {})
                .filter(d => d !== 'unknown')
                .sort()
                .reverse();
            if (days.length > 0) {
                setSelectedDay(days[0]);
            }
        } catch (err) {
            console.error('Failed to fetch stats:', err);
            toast.error('Failed to load comparison data');
        } finally {
            setLoading(false);
        }
    };
    
    const toggleRaceExpanded = (eventId) => {
        setExpandedRaces(prev => ({
            ...prev,
            [eventId]: !prev[eventId]
        }));
    };
    
    if (loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center p-4">
                <div className="text-center space-y-8">
                    {/* Racing Horse Animation */}
                    <div className="relative w-80 h-32 mx-auto">
                        {/* Track */}
                        <div className="absolute bottom-4 left-0 right-0 h-2 bg-gradient-to-r from-amber-900/50 via-amber-700/50 to-amber-900/50 rounded-full" />
                        <div className="absolute bottom-3 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-amber-500/30 to-transparent" />
                        
                        {/* Podium Human Horse */}
                        <div className="absolute bottom-6 animate-[raceHorseBlue_3s_ease-in-out_infinite]">
                            <div className="relative">
                                <div className="text-4xl transform -scale-x-100">🏇</div>
                                <div className="absolute -top-1 -right-1 w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center">
                                    <Users className="h-2.5 w-2.5 text-white" />
                                </div>
                            </div>
                        </div>
                        
                        {/* AI Horse */}
                        <div className="absolute bottom-6 animate-[raceHorsePurple_3s_ease-in-out_infinite_0.2s]">
                            <div className="relative">
                                <div className="text-4xl transform -scale-x-100">🏇</div>
                                <div className="absolute -top-1 -right-1 w-4 h-4 bg-purple-500 rounded-full flex items-center justify-center">
                                    <Bot className="h-2.5 w-2.5 text-white" />
                                </div>
                            </div>
                        </div>
                        
                        {/* Finish Line */}
                        <div className="absolute right-4 bottom-0 top-8 w-1 bg-gradient-to-b from-white via-black to-white bg-[length:100%_8px] bg-repeat-y opacity-60" />
                    </div>
                    
                    {/* Loading Text */}
                    <div className="space-y-3">
                        <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-500 via-purple-500 to-blue-500 bg-clip-text text-transparent animate-pulse">
                            Analyzing Race Data...
                        </h2>
                        <p className="text-muted-foreground">
                            Comparing Podium predictions vs AI insights
                        </p>
                    </div>
                    
                    {/* Progress Dots */}
                    <div className="flex justify-center gap-2">
                        <div className="w-3 h-3 bg-blue-500 rounded-full animate-[bounce_1s_ease-in-out_infinite]" />
                        <div className="w-3 h-3 bg-purple-500 rounded-full animate-[bounce_1s_ease-in-out_infinite_0.2s]" />
                        <div className="w-3 h-3 bg-blue-500 rounded-full animate-[bounce_1s_ease-in-out_infinite_0.4s]" />
                    </div>
                    
                    {/* Stats Loading Preview */}
                    <div className="grid grid-cols-2 gap-4 max-w-md mx-auto">
                        <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg animate-pulse">
                            <Users className="h-6 w-6 text-blue-500 mx-auto mb-2" />
                            <div className="h-4 bg-blue-500/20 rounded w-24 mx-auto" />
                        </div>
                        <div className="p-4 bg-purple-500/10 border border-purple-500/20 rounded-lg animate-pulse">
                            <Bot className="h-6 w-6 text-purple-500 mx-auto mb-2" />
                            <div className="h-4 bg-purple-500/20 rounded w-24 mx-auto" />
                        </div>
                    </div>
                </div>
                
                {/* Custom Animation Keyframes */}
                <style>{`
                    @keyframes raceHorseBlue {
                        0% { left: 0%; }
                        50% { left: 70%; }
                        100% { left: 0%; }
                    }
                    @keyframes raceHorsePurple {
                        0% { left: 5%; }
                        45% { left: 65%; }
                        55% { left: 75%; }
                        100% { left: 5%; }
                    }
                `}</style>
            </div>
        );
    }
    
    if (!stats || stats.total_races === 0) {
        return (
            <div className="container mx-auto px-4 py-8 max-w-4xl" data-testid="experts-vs-ai-page">
                <div className="text-center py-12">
                    <Bot className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <h2 className="text-xl font-heading font-semibold mb-2">No AI Picks Yet</h2>
                    <p className="text-muted-foreground mb-6 text-sm">
                        Generate AI picks to start the competition!
                    </p>
                    <Button onClick={() => navigate('/admin/ai-settings')} variant="outline" size="sm">
                        Go to AI Settings
                    </Button>
                </div>
            </div>
        );
    }
    
    const days = Object.keys(stats.daily_breakdown || {})
        .filter(d => d !== 'unknown')
        .sort()
        .reverse();
    const dayData = selectedDay ? stats.daily_breakdown[selectedDay] : null;
    
    return (
        <div className="container mx-auto px-4 py-6 sm:py-8 max-w-4xl" data-testid="experts-vs-ai-page">
            {/* Header */}
            <div className="mb-6 sm:mb-8 text-center">
                <div className="flex items-center justify-center gap-3 mb-2">
                    <Users className="h-5 w-5 sm:h-6 sm:w-6 text-muted-foreground" />
                    <span className="text-muted-foreground font-medium">vs</span>
                    <Bot className="h-5 w-5 sm:h-6 sm:w-6 text-muted-foreground" />
                </div>
                <h1 className="text-xl sm:text-2xl font-heading font-bold mb-1 page-title">Podium vs AI</h1>
                <p className="text-muted-foreground text-xs sm:text-sm">
                    Who picks winners better? Track correct predictions by category.
                </p>
            </div>
            
            {/* Overall Score */}
            <div className="mb-6 sm:mb-8">
                <OverallScoreCard stats={stats} />
            </div>
            
            {/* Category Cards */}
            <div className="mb-6 sm:mb-8">
                <h2 className="text-base sm:text-lg font-heading font-semibold mb-3 sm:mb-4 flex items-center gap-2">
                    <Target className="h-4 w-4 text-primary" />
                    Category Breakdown
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                    <CategoryCard 
                        category="top_pick"
                        aiStats={stats.ai.top_pick}
                        expertStats={stats.experts.top_pick}
                    />
                    <CategoryCard 
                        category="each_way"
                        aiStats={stats.ai.each_way}
                        expertStats={stats.experts.each_way}
                    />
                    <CategoryCard 
                        category="dark_horse"
                        aiStats={stats.ai.dark_horse}
                        expertStats={stats.experts.dark_horse}
                    />
                </div>
            </div>
            
            {/* Daily Breakdown */}
            <Card>
                <CardHeader className="pb-3 sm:pb-4">
                    <CardTitle className="font-heading text-base sm:text-lg flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-primary" />
                        Race by Race
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {/* Day selector - horizontal scroll on mobile */}
                    <div className="flex gap-2 overflow-x-auto pb-2 mb-4 sm:mb-5 sm:flex-wrap sm:overflow-visible">
                        {days.map(day => {
                            const dateLabel = new Date(day + 'T12:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
                            return (
                                <Button
                                    key={day}
                                    variant={selectedDay === day ? "default" : "outline"}
                                    size="sm"
                                    onClick={() => setSelectedDay(day)}
                                    className="shrink-0 text-xs sm:text-sm"
                                >
                                    {dateLabel}
                                </Button>
                            );
                        })}
                    </div>
                    
                    {/* Day summary */}
                    {dayData && (
                        <div className="mb-3 sm:mb-4 p-2.5 sm:p-3 bg-muted/30 rounded-lg flex items-center justify-between text-xs sm:text-sm">
                            <span className="text-muted-foreground">{dayData.races?.length || 0} races</span>
                            <div className="flex items-center gap-3 sm:gap-4">
                                <div className="flex items-center gap-1.5">
                                    <Users className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-muted-foreground" />
                                    <span className="font-data font-semibold">{dayData.experts?.correct || 0}</span>
                                </div>
                                <span className="text-muted-foreground/50">vs</span>
                                <div className="flex items-center gap-1.5">
                                    <span className="font-data font-semibold">{dayData.ai?.correct || 0}</span>
                                    <Bot className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-muted-foreground" />
                                </div>
                            </div>
                        </div>
                    )}
                    
                    {/* Race list */}
                    <div className="space-y-1">
                        {dayData?.races?.map(race => (
                            <RaceResultRow 
                                key={race.event_id}
                                race={race}
                                expanded={expandedRaces[race.event_id]}
                                onToggle={() => toggleRaceExpanded(race.event_id)}
                            />
                        ))}
                    </div>
                </CardContent>
            </Card>
            
            {/* Scoring Rules */}
            <div className="mt-4 sm:mt-6 text-center text-[10px] sm:text-xs text-muted-foreground">
                <p className="flex items-center justify-center gap-3 sm:gap-4 flex-wrap">
                    <span className="flex items-center gap-1">
                        <Trophy className="h-3 w-3" />
                        Top Pick: Must WIN
                    </span>
                    <span className="flex items-center gap-1">
                        <Medal className="h-3 w-3" />
                        Each-Way: Must PLACE
                    </span>
                    <span className="flex items-center gap-1">
                        <Zap className="h-3 w-3" />
                        Dark Horse: Must WIN
                    </span>
                </p>
            </div>
        </div>
    );
}
